import { PointCloudObject } from "@/object-cache";
import {
  PointCloudAnalysis,
  PointCloudAnalysisColormap,
  PointCloudAnalysisPlane,
  ReferencePlaneType,
  setAnalysisSearchDistanceAndFittedPlanes,
  setCadAnalysisRange,
  setAnalysisVolumeBasePlaneElevation,
  setVolumeAnalysisResult,
  VolumeAnalysisBasePlaneType,
  type FittedPlanesMap,
} from "@/store/point-cloud-analysis-tool-slice";
import { AppDispatch } from "@/store/store";
import { grey } from "@faro-lotv/flat-ui";
import { generateGUID } from "@faro-lotv/foundation";
import {
  averageUnsignedDistance,
  ColormapParameters,
  estimateVolume,
  findPlanes,
  getLotvMath,
  PointsSelectionResult,
  polygonToArray,
  RAD_TO_DEG,
  selectPointsByPolygon,
  selectPointsInSphere,
  VolumeEstimationResult,
  VolumeMeshGeometry,
} from "@faro-lotv/lotv";
import { Camera } from "@react-three/fiber";
import { Color, Object3D, Plane, Sphere, Vector3 } from "three";

/**
 * Adjust the polygon selection points to be coplanar following these steps:
 *  - A set of maximal 50k points from the point cloud are selected using the polygon
 *  - Try to find max 2 planes in the selected points using RANSAC method
 *  - Choose the plane with the smallest average unsigned distance to the original polygon
 *  - The original polygon points are projected onto the plane, and returned as result
 *
 * @param polygon Polygon points to be adjusted
 * @param pointCloud The point cloud object
 * @returns A new array of adjusted polygon points
 */
export async function adjustPointsToBeCoplanar(
  polygon: Vector3[] | undefined,
  pointCloud: PointCloudObject,
): Promise<Vector3[] | undefined> {
  // no adjustment needed if less than 3 points
  if (!polygon) return;

  // make a copy of the input array
  const points = polygon.slice();
  if (points.length < 3) return points;

  // Do a rough selection of points by the polygon
  //  - use a big plane threshold (1 meter) to cover more area
  //  - limit the number of points to 50k
  const selection = await selectPointsByPolygon(pointCloud, {
    polygon: points,
    planeThreshold: 1.0,
    maxNumberOfPoints: 50_000,
  });
  if (!selection) return points;

  // A much smaller RANSAC threshold is used to fit plane to the selected points
  // +/-5mm is a reasonable threshold for a flat surface, roughly 2 times the point density
  // Try to find max 2 planes from the selection
  const PLANE_RANSAC_THRESHOLD = 0.005;
  const fitPlanes = await findPlanes(
    selection.points,
    PLANE_RANSAC_THRESHOLD,
    true,
    2,
    0.1,
  );
  if (!fitPlanes || fitPlanes.length < 1) return points;

  // Create reference plane, offset result to the origin
  let refPlane = new Plane().setFromNormalAndCoplanarPoint(
    fitPlanes[0].normal,
    fitPlanes[0].point.add(selection.origin),
  );

  // If found 2 planes, choose the one with the smallest average unsigned distance
  if (fitPlanes.length === 2) {
    const plane = new Plane().setFromNormalAndCoplanarPoint(
      fitPlanes[1].normal,
      fitPlanes[1].point.add(selection.origin),
    );
    if (
      averageUnsignedDistance(plane, points) <
      averageUnsignedDistance(refPlane, points)
    ) {
      refPlane = plane;
    }
  }

  return points.map((p) => refPlane.projectPoint(p, new Vector3()));
}

/**
 * Default dearch distance (+/-) from the reference plane, used to collect analysis points
 * The selected points will be kept between two parallel planes with this distance.
 */
export const ANALYSIS_SEARCH_DISTANCE_DEFAULT = 0.05;

/**
 * Create a new point cloud analysis from a polygon selection.
 *
 * @param pointcloud Point cloud object to analyze.
 * @param polygon Polygon defining the region to analyze.
 * @param camera Current camera.
 * @returns New analysis object.
 */
export async function createNewAnalysis(
  pointcloud: PointCloudObject,
  polygon: Vector3[],
  camera: Camera,
): Promise<PointCloudAnalysis> {
  // The world space direction in which the camera is looking when the analysis is created by user.
  // The fit plane normal will be aligned along the oppsite direction.
  const referenceDirection = camera.getWorldDirection(new Vector3()).negate();

  // Default parameters for new analysis, set to reasonable values for most cases
  const tolerance = 0.01;
  const searchDistance = ANALYSIS_SEARCH_DISTANCE_DEFAULT;
  const { fittedPlane, fittedPlumbPlane } = await computeReferencePlanes(
    pointcloud,
    polygon,
    referenceDirection,
    searchDistance,
  );

  // The default reference plane type is determined according to the fitted plane
  //  - If plane is almost (e.g. within 15 deg) horizontal, use the “Level” fit
  //  - If plane is almost (e.g. within 15 deg) vertical, use the “Plumb” fit
  //  - Use the “Flatness” fit otherwise
  const levelAngleThreshold = 15;
  const plumbAngleThresholdMin = 75;
  const plumbAngleThresholdMax = 105;
  let referencePlaneType = ReferencePlaneType.flatness;
  if (fittedPlane) {
    const fittedPlaneNormal = new Vector3().fromArray(fittedPlane.normal);
    const angle = fittedPlaneNormal.angleTo(Object3D.DEFAULT_UP) * RAD_TO_DEG;
    if (angle < levelAngleThreshold) {
      referencePlaneType = ReferencePlaneType.levelness;
    } else if (
      angle > plumbAngleThresholdMin &&
      angle < plumbAngleThresholdMax
    ) {
      referencePlaneType = ReferencePlaneType.plumbness;
    }
  }

  // using the fitted plane center as the initial elevation
  const elevation = fittedPlane?.point[1] ?? 0;
  const planesMap: FittedPlanesMap = {};
  if (fittedPlane && fittedPlumbPlane) {
    planesMap[searchDistance] = { fittedPlane, fittedPlumbPlane };
  }

  return {
    id: generateGUID(),
    polygonSelection: polygon.map((p) => p.toArray()),
    tolerance,
    searchDistance,
    parentId: pointcloud.iElement.id,
    referencePlaneType,
    colormap: pointCloudAnalysisColormapPresets.rainbow,
    isVisible: true,
    elevation,
    fittedPlane,
    fittedPlumbPlane,
    fittedPlanesMap: planesMap,
    isDirty: true,
    labels: [],
    volumeBasePlaneType: VolumeAnalysisBasePlaneType.bestFitPolygonBase,
    volumeDisplayNbIsoLines: 5,
  };
}

/**
 * Update the search distance of a point cloud analysis.
 *
 * @param pointcloud The point cloud object to analyze.
 * @param analysis The point cloud analysis to update.
 * @param searchDistance The new search distance.
 * @param dispatch The Redux dispatch function.
 */
export async function updateAnalysisSearchDistance(
  pointcloud: PointCloudObject,
  analysis: PointCloudAnalysis,
  searchDistance: number,
  dispatch: AppDispatch,
): Promise<void> {
  if (analysis.kind === "cad") {
    dispatch(setCadAnalysisRange({ analysisId: analysis.id, searchDistance }));
    return;
  }
  if (!analysis.fittedPlane || !analysis.fittedPlumbPlane) {
    return;
  }

  // Check whether fitted planes were already computed for this search distance
  const existingPlanes = analysis.fittedPlanesMap[searchDistance];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (existingPlanes) {
    dispatch(
      setAnalysisSearchDistanceAndFittedPlanes({
        analysisId: analysis.id,
        searchDistance,
        fittedPlane: existingPlanes.fittedPlane,
        fittedPlumbPlane: existingPlanes.fittedPlumbPlane,
      }),
    );
    return;
  }

  // No fitted planes for this search distance, compute them and store into the fitted planes map
  const polygon = analysis.polygonSelection.map((p) =>
    new Vector3().fromArray(p),
  );
  const referenceDirection = new Vector3().fromArray(
    analysis.fittedPlane.normal,
  );
  const { fittedPlane, fittedPlumbPlane } = await computeReferencePlanes(
    pointcloud,
    polygon,
    referenceDirection,
    searchDistance,
  );

  dispatch(
    setAnalysisSearchDistanceAndFittedPlanes({
      analysisId: analysis.id,
      searchDistance,
      fittedPlane,
      fittedPlumbPlane,
    }),
  );
}

/**
 * Compute the reference planes for a point cloud analysis.
 *
 * @param pointcloud The point cloud object to analyze.
 * @param polygon Polygon defining the region to analyze.
 * @param referenceDirection Reference direction used to define the plane orientation.
 * @param searchDistance Search distance used to collect points for analysis.
 * @returns Reference flatness and plumbness planes.
 */
async function computeReferencePlanes(
  pointcloud: PointCloudObject,
  polygon: Vector3[],
  referenceDirection: Vector3,
  searchDistance: number,
): Promise<{
  fittedPlane: PointCloudAnalysisPlane | undefined;
  fittedPlumbPlane: PointCloudAnalysisPlane | undefined;
}> {
  const selection = await selectPointsByPolygon(pointcloud, {
    polygon,
    planeThreshold: searchDistance * 2,
    maxNumberOfPoints: 1_000_000,
  });
  // The collected points are not saved for later use. It is more efficient to compute
  // both flatness and plumbness planes here. Then there will be no need to re-collect
  // points and do computation again when user switch options later.
  const fittedPlane = await computeAnalysisFitPlane(
    selection,
    ReferencePlaneType.flatness,
    referenceDirection,
  );
  const fittedPlumbPlane = await computeAnalysisFitPlane(
    selection,
    ReferencePlaneType.plumbness,
    referenceDirection,
  );
  return { fittedPlane, fittedPlumbPlane };
}

/**
 * Compute the best-fit plane of a point cloud region defined by a polygon.
 *
 * @param selection The point cloud selection to analyze.
 * @param referencePlaneType The type of reference plane to compute.
 * @param referenceDirection Reference direction used to define the plane orientation.
 * @returns The normal and point of the best-fit plane.
 */
async function computeAnalysisFitPlane(
  selection: PointsSelectionResult | undefined,
  referencePlaneType: ReferencePlaneType,
  referenceDirection: Vector3,
): Promise<PointCloudAnalysisPlane | undefined> {
  if (!selection) return;

  const lotvMath = await getLotvMath();
  const fitResult =
    referencePlaneType === ReferencePlaneType.plumbness
      ? lotvMath.fitPlanePerpendicularTo(selection.points, "XZ")
      : lotvMath.fitPlane(selection.points);
  if (!fitResult) return;

  const normal = new Vector3(
    fitResult.normal.x,
    fitResult.normal.y,
    fitResult.normal.z,
  );
  // correct plane normal to be aligned with the reference
  if (normal.dot(referenceDirection) < 0) {
    normal.negate();
  }

  const point = new Vector3(
    fitResult.point.x + selection.origin.x,
    fitResult.point.y + selection.origin.y,
    fitResult.point.z + selection.origin.z,
  );

  return { normal: normal.toArray(), point: point.toArray() };
}

/** Reference plane definition consisting of a normal and a point on the plane. */
export type ReferencePlane = {
  normal: Vector3;
  point: Vector3;
};

/**
 * Compute the reference plane for a point cloud analysis.
 *
 * @param analysis The point cloud analysis to compute the reference plane for.
 * @returns The normal and point of the reference plane.
 */
export function getAnalysisReferencePlane(
  analysis: PointCloudAnalysis,
): ReferencePlane | undefined {
  if (analysis.kind === "cad") return;
  if (analysis.referencePlaneType === ReferencePlaneType.volume) {
    if (!analysis.volumeAnalysisResult) {
      return;
    }
    const normal = new Vector3().fromArray(
      analysis.volumeAnalysisResult.basePlaneNormal,
    );
    const point = new Vector3().fromArray(
      analysis.volumeAnalysisResult.basePlaneOrigin,
    );
    point.addScaledVector(normal, getVolumeBasePlaneHeightReference(analysis));
    if (analysis.volumeDisplayOffset) {
      point.addScaledVector(normal, analysis.volumeDisplayOffset);
    }
    return {
      normal,
      point,
    };
  }

  const fittedPlane =
    analysis.referencePlaneType === ReferencePlaneType.plumbness
      ? analysis.fittedPlumbPlane
      : analysis.fittedPlane;

  if (!fittedPlane) return;

  const point = new Vector3().fromArray(fittedPlane.point);
  const normal = new Vector3().fromArray(fittedPlane.normal);

  if (analysis.referencePlaneType === ReferencePlaneType.levelness) {
    normal.set(0.0, 1.0, 0.0);
  } else if (analysis.referencePlaneType === ReferencePlaneType.elevation) {
    normal.set(0.0, 1.0, 0.0);
    point.setY(analysis.elevation);
  }

  return { normal, point };
}

/**
 * Enum defines list of presets of colormaps which can be used for point clouds analysis
 */
export enum PointCloudAnalysisColormapPreset {
  rainbow = "rainbow",
  grayscale = "grayscale",
  blueGreenRed = "blueGreenRed",
  redGreenRed = "redGreenRed",
}

export type PointCloudAnalysisColormapPresetKey =
  keyof typeof PointCloudAnalysisColormapPreset;

/**
 * @param name The name to check
 * @returns True if the given name string is a colormap preset name
 */
export function isColormapPresetName(
  name: string,
): name is PointCloudAnalysisColormapPresetKey {
  return name in PointCloudAnalysisColormapPreset;
}

/**
 * Check if two colormaps are equal
 *
 * @param colormap1 The first colormap to compare
 * @param colormap2 The second colormap to compare
 * @returns True if the two colormaps are equal
 */
export function areColormapsSame(
  colormap1: PointCloudAnalysisColormap,
  colormap2: PointCloudAnalysisColormap,
): boolean {
  if (colormap1.length !== colormap2.length) return false;
  for (let i = 0; i < colormap1.length; i++) {
    if (colormap1[i].value !== colormap2[i].value) return false;
    if (colormap1[i].color !== colormap2[i].color) return false;
  }
  return true;
}

/**
 * List of pre-defined colormap colors.
 *
 * Note:
 * - Two consecutive equal values indicates a color jump at this value.
 * - values go from 0.0 (minimum range) to 1.0 (maximum range)
 * - value 0.5 is 0.0 deviation IF minium and maximum absolute deviations are equal
 */
export const pointCloudAnalysisColormapPresets: Record<
  PointCloudAnalysisColormapPresetKey,
  PointCloudAnalysisColormap
> = {
  rainbow: [
    { value: 0.0, color: "#FF00FF" },
    { value: 0.0, color: "#0000FF" },
    { value: 0.25, color: "#00FFFF" },
    { value: 0.5, color: "#00FF00" },
    { value: 0.75, color: "#FFFF00" },
    { value: 1.0, color: "#FF5400" },
    { value: 1.0, color: "#FF0000" },
  ],
  grayscale: [
    { value: 0.0, color: grey[1000] },
    { value: 1.0, color: grey[50] },
  ],
  blueGreenRed: [
    { value: 0.0, color: "#0000FF" },
    { value: 0.0, color: "#00FF00" },
    { value: 1.0, color: "#00FF00" },
    { value: 1.0, color: "#FF0000" },
  ],
  redGreenRed: [
    { value: 0.0, color: "#FF0000" },
    { value: 0.0, color: "#00FF00" },
    { value: 1.0, color: "#00FF00" },
    { value: 1.0, color: "#FF0000" },
  ],
};

/**
 * Compute the colormap parameters for a point cloud analysis.
 *
 * @param pointcloud The point cloud object to analyze.
 * @param analysis The point cloud analysis to compute the colormap parameters for.
 * @returns The colormap parameters.
 */
export function computeColormapParameters(
  pointcloud: PointCloudObject,
  analysis: PointCloudAnalysis,
): ColormapParameters | undefined {
  if (analysis.kind === "cad") return;
  const referencePlane = getAnalysisReferencePlane(analysis);
  if (!referencePlane) return;

  pointcloud.updateMatrixWorld();
  const transform = pointcloud.matrixWorld.clone().invert();
  const polygon = analysis.polygonSelection.map((p) =>
    new Vector3().fromArray(p),
  );

  const colormap = analysis.colormap.map(({ value, color }) => ({
    value,
    color: new Color(color),
  }));

  return {
    planeNormal: referencePlane.normal,
    planePoint: referencePlane.point,
    polygon,
    colormap,
    minDeviation: -analysis.tolerance,
    maxDeviation: analysis.tolerance,
    colorizationDistance: analysis.searchDistance,
    transform,
  };
}

/**
 * Given the picked point, compute label position as average position
 * of all points within 1 inch radius from the picked point.
 *
 * @param pointcloud Point cloud object associated with the analysis.
 * @param pickedPoint Picked label position.
 * @returns The label position
 */
export function computeLabelPosition(
  pointcloud: PointCloudObject,
  pickedPoint: Vector3,
): Vector3 | undefined {
  const selectionRadius = 0.0254;
  const sphere = new Sphere(pickedPoint, selectionRadius);
  const selection = selectPointsInSphere(pointcloud, {
    sphere,
    maxNumberOfPoints: 1000,
  });
  if (!selection) return;

  const average = new Vector3();
  const point = new Vector3();
  const nbPoints = selection.points.length / 3;
  for (let i = 0; i < nbPoints; i++) {
    point.fromArray(selection.points, i * 3);
    average.add(point);
  }
  average.divideScalar(nbPoints);
  average.add(selection.origin);

  return average;
}

// 5 cm grid cell size for volume estimation
const VOLUME_COMPUTE_CELL_SIZE = 0.05;

// 25 cm grid cell size for volume preview mesh, which is much faster to
// compute than cell size used for volume estimation. Just good enough
// quick preview of the volume mesh.
const VOLUME_PREVIEW_CELL_SIZE = 0.25;

// 10 million cells limit to avoid excessive memory usage and computation time
const VOLUME_MAX_NUMBER_OF_CELLS = 10_000_000;

/**
 * Update the volume analysis result for a point cloud analysis.
 *
 * @param pointcloud The point cloud object to analyze.
 * @param analysis The point cloud analysis to compute the volume analysis for.
 * @param volumeBasePlaneType The base plane type to compute the volume from.
 * @param dispatch The Redux dispatch function.
 * @param clippingPlanes The clipping planes to exclude points with, defaulting
 * to the ones currently applied to the point cloud material.
 */
export async function computeVolumeAnalysis(
  pointcloud: PointCloudObject,
  analysis: PointCloudAnalysis,
  volumeBasePlaneType: VolumeAnalysisBasePlaneType,
  dispatch: AppDispatch,
  clippingPlanes?: Plane[],
): Promise<void> {
  const result = await computeEstimatedVolume(
    pointcloud,
    analysis,
    volumeBasePlaneType,
    VOLUME_COMPUTE_CELL_SIZE,
    false,
    clippingPlanes,
  );
  if (!result) return;

  dispatch(
    setVolumeAnalysisResult({
      analysisId: analysis.id,
      volumeAnalysisResult: {
        basePlaneOrigin: result.referencePlaneOrigin.toArray(),
        basePlaneNormal: result.referencePlaneNormal.toArray(),
        baseArea: result.volumeResult.area,
        volume: result.volumeResult.volume,
        heightMin: result.volumeResult.heightMin,
        heightMax: result.volumeResult.heightMax,
      },
    }),
  );

  // set default level elevation
  dispatch(
    setAnalysisVolumeBasePlaneElevation({
      analysisId: analysis.id,
      elevation: result.referencePlaneOrigin.y,
    }),
  );
}

/**
 * Compute the volume mesh geometry for visualization of a point cloud volume analysis.
 *
 * @param pointcloud The point cloud object to analyze.
 * @param analysis The point cloud analysis to compute the volume analysis for.
 * @param clippingPlanes The clipping planes to exclude points with, defaulting
 * to the ones currently applied to the point cloud material.
 * @returns The volume mesh geometry or undefined if the analysis estimation failed.
 */
export async function computeVolumeMesh(
  pointcloud: PointCloudObject,
  analysis: PointCloudAnalysis,
  clippingPlanes?: Plane[],
): Promise<VolumeMeshGeometry | undefined> {
  const result = await computeEstimatedVolume(
    pointcloud,
    analysis,
    analysis.volumeBasePlaneType ??
      VolumeAnalysisBasePlaneType.bestFitPolygonBase,
    VOLUME_PREVIEW_CELL_SIZE,
    true,
    clippingPlanes,
  );
  return result?.volumeResult.meshGeometry;
}

/**
 * Extended volume estimation result including reference plane info.
 */
type ComputeVolumeResult = {
  referencePlaneOrigin: Vector3;
  referencePlaneNormal: Vector3;
  volumeResult: VolumeEstimationResult;
};

/**
 * Compute the volume of the point cloud within the polygon selection.
 *
 * @param pointcloud Point cloud object to analyze.
 * @param analysis The point cloud analysis to compute the volume for.
 * @param volumeBasePlaneType The base plane type to compute the volume from.
 * @param cellSize The cell size to use for volume estimation.
 * Smaller cell size will result in more accurate estimation but longer computation time.
 * @param createMeshGeometry Whether to create volume mesh geometry, will take more time.
 * @param clippingPlanes The clipping planes to exclude points with, defaulting
 * to the ones currently applied to the point cloud material.
 * @returns The volume estimation result or undefined if estimation failed.
 */
async function computeEstimatedVolume(
  pointcloud: PointCloudObject,
  analysis: PointCloudAnalysis,
  volumeBasePlaneType: VolumeAnalysisBasePlaneType,
  cellSize: number,
  createMeshGeometry: boolean,
  clippingPlanes?: Plane[],
): Promise<ComputeVolumeResult | undefined> {
  if (analysis.kind === "cad") return;
  const lotvMath = await getLotvMath();
  const polygon = analysis.polygonSelection.map((p) =>
    new Vector3().fromArray(p),
  );
  const plane = lotvMath.fitPlane(polygonToArray(polygon));
  if (!plane) return;

  const normal = new Vector3(0, 1, 0);
  if (volumeBasePlaneType === VolumeAnalysisBasePlaneType.bestFitPolygonBase) {
    normal.set(plane.normal.x, plane.normal.y, plane.normal.z);

    if (analysis.fittedPlane) {
      // The analysis.fittedPlane's normal orientation was corrected based on the
      // the direction in which the camera is looking when the analysis is created.
      // The volume up direction will be aligned along the this direction.
      const referenceDirection = new Vector3().fromArray(
        analysis.fittedPlane.normal,
      );
      if (normal.dot(referenceDirection) < 0) {
        normal.negate();
      }
    }
  }

  const options = {
    polygon,
    referencePlaneOrigin: new Vector3(
      plane.point.x,
      plane.point.y,
      plane.point.z,
    ),
    referencePlaneNormal: normal,
    cellSize,
    createMeshGeometry,
    maxNumberOfCells: VOLUME_MAX_NUMBER_OF_CELLS,
    // Exclude the points hidden by the active clipping box (SWEB-8780)
    clippingPlanes:
      clippingPlanes ?? pointcloud.material.clippingPlanes ?? undefined,
  };
  const result = estimateVolume(pointcloud, options);
  return {
    referencePlaneOrigin: options.referencePlaneOrigin,
    referencePlaneNormal: options.referencePlaneNormal,
    volumeResult: result,
  };
}

/**
 * Get the height reference for the volume base plane.
 *
 * @param analysis The point cloud analysis to get the height reference from.
 * @returns The height reference value.
 */
export function getVolumeBasePlaneHeightReference(
  analysis: PointCloudAnalysis,
): number {
  if (!analysis.volumeAnalysisResult) return 0.0;

  switch (analysis.volumeBasePlaneType) {
    case VolumeAnalysisBasePlaneType.levelLowestPointBase:
      return analysis.volumeAnalysisResult.heightMin;
    case VolumeAnalysisBasePlaneType.levelHighestPointBase:
      return analysis.volumeAnalysisResult.heightMax;
    case VolumeAnalysisBasePlaneType.levelAtElevation: {
      if (analysis.volumeBasePlaneElevation === undefined) {
        return 0.0;
      }
      const heightReference =
        analysis.volumeBasePlaneElevation -
        analysis.volumeAnalysisResult.basePlaneOrigin[1];
      return heightReference;
    }
    default:
      return 0.0;
  }
}
